import { useEffect, useMemo, useState } from 'react';

import { createContexts } from '@/js/libs/create-contexts';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { useModalState } from '@/js/libs/hooks/useModalState';
import { useLocation } from '@/js/libs/router';

import useLocalStorage, { StorageKey } from 'src/hooks/useLocalStorage.ts';
import { clearTrackedKeys, disableTracking, enableTracking, subscribeToTrackedKeys } from './tracker/index.ts';
import type { TrackedTranslation } from './types.ts';

export const { useTranslateModeContext } = createContexts({
  TranslateMode() {
    const localStorage = useLocalStorage();
    const location = useLocation();

    const editorModalState = useModalState<TrackedTranslation>();

    const [isEnabled, setIsEnabled] = useState(localStorage.loadString(StorageKey.TRANSLATE_MODE_ENABLED) === 'true');
    const [isOverlayOpen, setIsOverlayOpen] = useState(false);
    const [trackedKeys, setTrackedKeys] = useState<string[]>([]);

    const toggleEnabled = useCallbackRef(() => {
      setIsEnabled((prev) => !prev);
    });

    const openOverlay = useCallbackRef(() => {
      setIsOverlayOpen(true);
    });

    const closeOverlay = useCallbackRef(() => {
      setIsOverlayOpen(false);
    });

    useEffect(() => {
      if (isEnabled) {
        enableTracking();
      } else {
        disableTracking();
        setIsOverlayOpen(false);
      }
    }, [isEnabled]);

    useEffect(() => {
      const unsubscribe = subscribeToTrackedKeys((keys) => {
        setTrackedKeys(Array.from(keys));
      });
      return unsubscribe;
    }, []);

    useEffect(() => {
      if (isEnabled) {
        clearTrackedKeys();
      }
    }, [location.pathname, isEnabled]);

    useEffect(() => {
      localStorage.saveString(StorageKey.TRANSLATE_MODE_ENABLED, String(isEnabled));
    }, [isEnabled, localStorage]);

    return useMemo(
      () => ({
        editorModalState,
        isEnabled,
        isOverlayOpen,
        trackedKeys,
        toggleEnabled,
        openOverlay,
        closeOverlay,
      }),
      [editorModalState, isEnabled, isOverlayOpen, trackedKeys, toggleEnabled, openOverlay, closeOverlay],
    );
  },
});
